import React, {useCallback} from 'react';

import {ChunkedDownloadTool} from 'src/components/chuncked-download-tool';
import {exportUsers} from 'src/openapi/admin/apis/UsersExportApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    USER_EXPORT_FILE_NAME,
    USER_EXPORT_HEADERS,
    USER_EXPORT_TASK_NAME,
} from './constants';

import type {ReactNode} from 'react';
import type {GetData} from 'src/components/chuncked-download-tool/typedef';
import type {UserExportInfo} from 'src/openapi/admin/models/UserExportInfo';

type Props = {
    userIds: number[],
    children: (handler: (ids?: number[]) => void) => ReactNode,
};

export const UserExportDownloadTool = ({userIds, children}: Props) => {
    const apiDispatch = useAppDispatch();

    const getData: GetData<Record<string, string>> = useCallback(
        async (ids: number[]) => {
            const result = await apiDispatch(
                exportUsers({requestBody: ids}),
            );

            if (result.payload.success) {
                const idOrder = new Map(ids.map((id, index) => [id, index]));
                const sorted = [...result.payload.data].sort(
                    (a: UserExportInfo, b: UserExportInfo) =>
                        (idOrder.get(a.userId) ?? 0) - (idOrder.get(b.userId) ?? 0),
                );
                const data = sorted.map(
                    (user: UserExportInfo) => ({
                        email: user.email ?? '',
                        firstName: user.firstName ?? '',
                        lastName: user.lastName ?? '',
                        accountName: user.accountName ?? '',
                        vendorName: user.vendor?.vendorName ?? '',
                        role: user.role?.name ?? '',
                        tools: user.tools?.map(t => t.name).join(', ') ?? '',
                    }),
                );

                return {
                    payload: {
                        success: true as const,
                        error: false as const,
                        data,
                    },
                };
            }

            return {
                payload: {
                    success: false as const,
                    error: true as const,
                    data: result.payload.data,
                },
            };
        },
        [apiDispatch],
    );

    return (
        <ChunkedDownloadTool
            ids={userIds}
            taskName={USER_EXPORT_TASK_NAME}
            fileName={USER_EXPORT_FILE_NAME}
            fileHeaders={USER_EXPORT_HEADERS}
            getData={getData}
            chunkSize={250}>
            {children}
        </ChunkedDownloadTool>
    );
};
